/**
 * Client-side amount handling without floats (AGENTS.md): amounts are bigint minor units
 * (2 decimals for INR and AED). Arithmetic typed into amount fields (`120*1.05`) is evaluated with
 * scaled bigints and rounded half away from zero to 2 decimals.
 */
import { toLatinDigits } from './arabic';
import { groupDigits } from './org-locale';

const SCALE = 10n ** 12n; // working precision for expressions

/** "1,234.5" → 123450n (fils); "" → 0n; invalid → null. */
export function toMinor(text: string, exp = 2): bigint | null {
  const s = text.replace(/,/g, '').trim();
  if (s === '') return 0n;
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return null;
  const frac = (m[3] ?? '').padEnd(exp + 1, '0');
  let minor = BigInt((m[2] || '0') + frac.slice(0, exp));
  if (Number(frac[exp]) >= 5) minor += 1n; // half up on the first dropped digit
  return m[1] ? -minor : minor;
}

/** 123450n → "1234.50" (plain decimal for the API). */
export function minorToPlain(minor: bigint, exp = 2): string {
  const neg = minor < 0n;
  const s = (neg ? -minor : minor).toString().padStart(exp + 1, '0');
  if (exp === 0) return `${neg ? '-' : ''}${s}`;
  return `${neg ? '-' : ''}${s.slice(0, s.length - exp)}.${s.slice(s.length - exp)}`;
}

/** 123450n → "1,234.50" (document 05: leading minus for negatives; lakh/crore for India). */
export function formatMinor(minor: bigint, exp = 2): string {
  const plain = minorToPlain(minor < 0n ? -minor : minor, exp);
  const [int, frac] = plain.split('.') as [string, string?];
  return `${minor < 0n ? '-' : ''}${groupDigits(int)}${frac === undefined ? '' : `.${frac}`}`;
}

// ---- expression evaluation: + - * / and parentheses over decimals ----
type Tok = { t: 'num'; v: bigint } | { t: 'op'; v: string };

function tokenize(src: string): Tok[] | null {
  const out: Tok[] = [];
  const s = src.replace(/,/g, '').replace(/\s+/g, '');
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if ('+-*/()'.includes(ch)) {
      out.push({ t: 'op', v: ch });
      i += 1;
      continue;
    }
    // "5." and ".5" are numbers, as toMinor reads them
    const m = /^(?:\d+(?:\.\d*)?|\.\d+)/.exec(s.slice(i));
    if (!m) return null;
    const [int, frac = ''] = m[0].split('.') as [string, string?];
    const digits = (frac ?? '').slice(0, 12).padEnd(12, '0');
    out.push({ t: 'num', v: BigInt(int || '0') * SCALE + BigInt(digits) });
    i += m[0].length;
  }
  return out;
}

/** Evaluates an amount expression; returns minor units or null when it is not valid. */
export function evaluateAmount(src: string, exp = 2): bigint | null {
  // Arabic-Indic digits and separators read as 0-9 (document 15, section 5.2)
  const toks = tokenize(toLatinDigits(src));
  if (!toks || toks.length === 0) return null;
  let p = 0;
  const peek = () => toks[p];
  const expr = (): bigint | null => {
    let left = term();
    while (left !== null && peek()?.t === 'op' && (peek()!.v === '+' || peek()!.v === '-')) {
      const op = toks[p++]!.v;
      const right = term();
      if (right === null) return null;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  };
  const term = (): bigint | null => {
    let left = factor();
    while (left !== null && peek()?.t === 'op' && (peek()!.v === '*' || peek()!.v === '/')) {
      const op = toks[p++]!.v;
      const right = factor();
      if (right === null || (op === '/' && right === 0n)) return null;
      left = op === '*' ? (left * right) / SCALE : (left * SCALE) / right;
    }
    return left;
  };
  const factor = (): bigint | null => {
    const tok = toks[p++];
    if (!tok) return null;
    if (tok.t === 'num') return tok.v;
    if (tok.v === '-' || tok.v === '+') {
      const v = factor();
      return v === null ? null : tok.v === '-' ? -v : v;
    }
    if (tok.v === '(') {
      const v = expr();
      if (toks[p++]?.v !== ')') return null;
      return v;
    }
    return null;
  };
  const value = expr();
  if (value === null || p !== toks.length) return null;
  const unit = SCALE / 10n ** BigInt(exp);
  const neg = value < 0n;
  const abs = neg ? -value : value;
  const rounded = (abs + unit / 2n) / unit;
  return neg ? -rounded : rounded;
}
